import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { IoClose } from "react-icons/io5";
import { CiFilter } from "react-icons/ci";
import DatePickerInput from "@/components/v-pos/inputs/date-picker-input.component";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FC, useState } from "react";
import { filterType } from "../_type/filter.type";
import { isAnyStringFieldValid } from "@/lib/helper";
import { DialogDescription, DialogTitle } from "@/components/ui/dialog";
import FormattedNumberInput from "@/components/v-pos/inputs/formatted-number-input.component";

const searchSchema = z.object({
  amount: z.string().min(2, "مبلغ را وارد کنید").or(z.literal("")),
  mobileQuery: z
    .string()
    .regex(/^[0-9]{0,11}$/, "شماره موبایل باید فقط شامل اعداد باشد")
    .or(z.literal("")),
  createdAt: z.string().min(2, "تاریخ را وارد کنید").or(z.literal("")),
});

type filterDrawerProps = {
  filterProps: Partial<filterType>;
  setFilterProps: (newFilters: Partial<filterType>) => void;
};

const FilterDrawer: FC<filterDrawerProps> = ({
  filterProps,
  setFilterProps,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const form = useForm({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      mobileQuery: filterProps?.mobileQuery ?? "",
      amount: filterProps?.amount ?? "",
      createdAt: filterProps?.createdAt ?? "",
    },
  });

  const onSubmit = (values: z.infer<typeof searchSchema>) => {
    setFilterProps({ ...values, page: 1 });
    setIsOpen(false);
  };

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerTrigger className="cursor-pointer">
        <CiFilter
          className={`text-[28px] ${
            isAnyStringFieldValid(filterProps)
              ? "text-primary"
              : "text-[#454545]"
          }`}
        />
      </DrawerTrigger>
      <DrawerContent className="px-3">
        <DialogTitle />
        <DialogDescription />
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-full max-w-[500px] mx-auto space-y-4 pt-3"
          >
            <div className="bg-[#ffffff] rounded-[12px] space-y-4">
              <FormField
                control={form.control}
                name="mobileQuery"
                render={({ field }) => (
                  <div className="flex relative">
                    <FormItem className="w-[93%]">
                      <FormControl>
                        <FormattedNumberInput
                          label="شماره موبایل"
                          inputMode="numeric"
                          maxLength={11}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-start text-[10px] pr-1" />
                    </FormItem>
                    <div className="w-[15px] h-[15px] flex justify-center items-center absolute left-0 top-4 bg-[#F7F7F7] rounded-full cursor-pointer">
                      <IoClose
                        color="#BB1515"
                        className="text-lg"
                        onClick={() => field.onChange("")}
                      />
                    </div>
                  </div>
                )}
              />
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <div className="flex relative">
                    <FormItem className="w-[93%]">
                      <FormControl>
                        <FormattedNumberInput label="مبلغ" {...field} />
                      </FormControl>
                      <FormMessage className="text-start text-[10px] pr-1" />
                    </FormItem>
                    <div className="w-[15px] h-[15px] flex justify-center items-center absolute left-0 top-4 bg-[#F7F7F7] rounded-full cursor-pointer">
                      <IoClose
                        color="#BB1515"
                        className="text-lg"
                        onClick={() => field.onChange("")}
                      />
                    </div>
                  </div>
                )}
              />
              <FormField
                control={form.control}
                name="createdAt"
                render={({ field }) => (
                  <div className="flex relative">
                    <FormItem className="w-[93%]">
                      <FormControl>
                        <DatePickerInput label="تاریخ" {...field} />
                      </FormControl>
                      <FormMessage className="text-start text-[10px] pr-1" />
                    </FormItem>
                    <div className="w-[15px] h-[15px] flex justify-center items-center absolute left-0 top-4 bg-[#F7F7F7] rounded-full cursor-pointer">
                      <IoClose
                        color="#BB1515"
                        className="text-lg"
                        onClick={() => field.onChange("")}
                      />
                    </div>
                  </div>
                )}
              />
            </div>

            <div className="flex flex-col justify-center gap-4 py-3">
              <Button type="submit" className="w-full font-bold">
                تایید
              </Button>
              <Button
                type="button"
                className="w-full font-bold"
                variant="outline"
                onClick={() => {
                  setIsOpen(false);
                }}
              >
                انصراف
              </Button>
            </div>
          </form>
        </Form>
      </DrawerContent>
    </Drawer>
  );
};

export default FilterDrawer;
